import { createRouter, createWebHistory } from "vue-router";

const Home = () => import("@/pages/Home.vue");
const AboutUs = () => import("@/pages/AboutUs.vue");
const ContactPage = () => import("@/pages/ContactPage.vue");
const ProductDetails = () => import("@/pages/ProductDetails.vue");
const ProductSearch = () => import("@/pages/ProductSearch.vue");
const ShoppingCart = () => import("@/pages/ShoppingCart.vue");
const ProductForm = () => import("@/pages/admin/components/ProductForm.vue");
const LoginForm = () => import("@/pages/LoginForm.vue");
const RegisterForm = () => import("@/pages/RegisterForm.vue");
const EmailVerificationPage = () => import("@/pages/EmailVerificationPage.vue");
const PasswordResetPage = () => import("@/pages/PasswordResetPage.vue");
const AdminPanel = () => import("@/pages/admin/AdminPanel.vue");
const AdminCategories = () => import("@/pages/admin/AdminCategories.vue");
const CreateCustomer = () => import("@/pages/admin/CreateCustomer.vue");
const MaintenancePage = () => import("@/components/maintenance/MaintenancePage.vue");
const TermsAndConditions = () => import("@/pages/TermsAndConditions.vue");

// Import the new BlogManager component
const BlogManager = () => import('@/pages/admin/BlogManager.vue')
// Import the new BlogEditor components
const BlogEditor = () => import('@/pages/admin/BlogEditor.vue')
// Import the new BlogCategories component
const BlogCategories = () => import('@/pages/admin/BlogCategories.vue')

const SITE_NAME = 'GOOD BARETO';

const routes = [ 
  {
    path: "/mantenimiento",
    name: "Maintenance",
    component: MaintenancePage,
    meta: {
      allowDuringMaintenance: true,
      title: 'Sitio en Mantenimiento'
    }
  },
  {
    path: "/",
    name: "Home",
    component: Home,
    meta: { title: 'Inicio' }
  },
  {
    path: "/nosotros",
    name: "AboutUs",
    component: AboutUs,
    meta: { title: 'Nosotros' }
  },
  {
    path: "/contacto",
    name: "Contact",
    component: ContactPage,
    meta: { title: 'Contacto' }
  },
  {
    path: "/configuracion",
    component: AdminPanel,
    meta: { requiresAuth: true, title: 'Configuración' },
    children: [
      {
        path: "",
        name: "UserDashboard",
        component: () => import('@/components/customer/CustomerDashboard.vue'),
        meta: { requiresAuth: true, title: 'Mi Panel' }
      },
      {
        path: "admin",
        name: "AdminOverview",
        component: () => import('@/pages/admin/AdminOverview.vue'),
        meta: { requiresAdmin: true, title: 'Panel de Administración' }
      },
      {
        path: "ventas",
        name: "AdminSalesHistory",
        component: () => import('@/pages/admin/AdminSalesHistory.vue'),
        meta: { 
          requiresAdmin: true,
          title: 'Historial de Ventas',
          // Añadimos esta propiedad para forzar que el componente siempre se vuelva a cargar
          keepAlive: false 
        }
      },
      {
        path: "ventas/carritos-abandonados",
        name: "AdminVentasCarritos",
        component: () => import('@/pages/admin/AdminVentasCarritos.vue'),
        meta: { requiresAdmin: true, title: 'Carritos pendientes y abandonados' }
      },
      {
        path: "stats",
        name: "AdminStats",
        component: () => import('@/pages/admin/AdminStats.vue'),
        meta: { requiresAdmin: true, title: 'Estadísticas' }
      },
      {
        path: "ventas/nueva",
        name: "admin-new-sale",
        component: () => import('@/pages/admin/AdminNewSale.vue'),
        meta: { requiresAdmin: true, title: 'Nueva Venta' }
      },
      {
        path: "productos",
        name: "AdminProductList",
        component: () => import('@/pages/admin/AdminProductList.vue'),
        meta: { requiresAdmin: true, title: 'Gestión de Productos' }
      },
      {
        path: "productos/agregar",
        name: "AddProduct",
        component: ProductForm,
        meta: { requiresAdmin: true, title: 'Agregar Producto' }
      },
      {
        path: "productos/editar/:productId",
        name: "ProductEdit",
        component: ProductForm,
        props: true,  
        meta: { requiresAdmin: true, title: 'Editar Producto' }
      },
      {
        path: "actividad",
        name: "AdminActivityPage",
        component: () => import('@/pages/admin/AdminActivity.vue'),
        meta: { requiresAdmin: true, title: 'Actividad' }
      },
      {
        path: "my-orders",
        name: "CustomerOrders",
        component: () => import('@/components/customer/CustomerOrders.vue'),
        meta: { requiresAuth: true, title: 'Mis Pedidos' }
      },
      {
        path: "mi-cuenta",
        name: "UserSettings",
        component: () => import('@/components/settings/UserSettings.vue'),
        meta: { requiresAuth: true, title: 'Mi Cuenta' }
      },
      {
        path: "direcciones",
        name: "ShippingAddresses",
        component: () => import('@/components/customer/ShippingAddresses.vue'),
        meta: { requiresAuth: true, title: 'Direcciones de Envío' }
      },
      {
        path: "categorias",
        name: "admin-categories",
        component: AdminCategories,
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Categorías'
        }
      },
      {
        path: "resenas",
        name: "admin-reviews",
        component: () => import('@/pages/admin/AdminReviews.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Reseñas'
        }
      },
      {
        path: "clientes",
        name: "admin-customers",
        component: () => import('@/pages/admin/AdminCustomersList.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Clientes'
        }
      },
      {
        path: '/configuracion/clientes',
        name: 'customers',
        component: () => import('@/pages/admin/AdminCustomersList.vue'),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'Clientes' }
      },
      {
        path: '/configuracion/clientes/crear',
        name: 'create-customer',
        component: CreateCustomer,
        meta: { requiresAuth: true, requiresAdmin: true, title: 'Crear Cliente' }
      },
      {
        path: 'distribuidores-pendientes',
        name: 'admin-distributor-pending',
        component: () => import('@/pages/admin/AdminDistributorPending.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Solicitudes distribuidor'
        }
      },
      {
        path: '/configuracion/clientes/:id',
        name: 'customer-profile',
        component: () => import('@/pages/admin/CustomerProfilePage.vue'),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'Perfil de Cliente' }
      },
      {
        path: '/configuracion/pedidos/:id',
        name: 'order-details',
        component: () => import('@/pages/admin/AdminOrderDetails.vue'),
        meta: { requiresAuth: true, title: 'Detalle de Pedido' }
      },
      {
        path: 'blog',
        name: 'BlogManager',
        component: BlogManager,
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Gestión de Blog'
        }
      },
      {
        path: 'blog/crear',
        name: 'BlogCreate',
        component: BlogEditor,
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Crear Artículo'
        }
      },
      {
        path: 'blog/editar/:id',
        name: 'BlogEdit',
        component: BlogEditor,
        props: true,
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Editar Artículo'
        }
      },
      {
        path: 'blog/categorias',
        name: 'BlogCategories',
        component: BlogCategories,
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Categorías del Blog'
        }
      },
      {
        path: 'coupons',
        name: 'AdminCoupons',
        component: () => import('@/pages/admin/AdminCoupons.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Cupones'
        }
      },
      {
        path: 'offers',
        name: 'AdminOffers',
        component: () => import('@/pages/admin/AdminOffers.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Ofertas'
        }
      },
      {
        path: 'scheduled-discounts',
        name: 'AdminScheduledDiscounts',
        component: () => import('@/pages/admin/AdminScheduledDiscounts.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Descuentos Programados'
        }
      },      {
        path: "mi-tienda",
        name: "StoreSettings",
        component: () => import('@/pages/admin/StoreSettings.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Mi Tienda'
        }
      },
      {
        path: "reviews",
        name: "ReviewsManager",
        component: () => import('@/pages/admin/ReviewsManager.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Gestión de Reseñas'
        }
      },
      {
        path: "empleados",
        name: "AdminEmployees",
        component: () => import('@/pages/admin/AdminEmployees.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Gestión de Empleados'
        }
      },
      {
        path: "roles",
        name: "AdminRoles",
        component: () => import('@/pages/admin/AdminRoles.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Roles y Permisos'
        }
      },
      {
        path: "envios",
        name: "AdminShipments",
        component: () => import('@/pages/admin/AdminShipments.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Gestión de Envíos'
        }
      },
      {
        path: "envios/config",
        name: "AdminShippingConfig",
        component: () => import('@/pages/admin/AdminShippingConfig.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Configuración de Envíos'
        }
      },
      {
        path: "envios/guia",
        name: "AdminShippingGuide",
        component: () => import('@/pages/admin/AdminShippingGuide.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Guía de MiCorreo'
        }
      }
    ]
  },
  {
    // Solo enteros positivos: evita que rutas tipo /producto/filter-facets disparen la vista de detalle.
    path: "/producto/:id(\\d+)",
    name: "ProductDetails",
    component: ProductDetails,
    meta: { title: 'Producto' }
  },
  {
    path: "/productos",
    name: "Products",
    component: ProductSearch,
    meta: { title: 'Productos' }
  },
  {
    path: "/blog",
    name: "Blog",
    component: () => import('@/pages/BlogPage.vue'),
    meta: { title: 'Blog' }
  },
  {
    path: "/terminos-y-condiciones",
    name: "TermsAndConditions",
    component: TermsAndConditions,
    meta: { title: 'Términos y Condiciones' }
  },
  {
    path: "/blog/:slug",
    name: "BlogPost",
    component: () => import('@/pages/BlogPost.vue'),
    props: true,
    meta: { title: 'Blog' }
  },
  {
    path: "/carrito/",
    name: "ShoppingCart",
    component: ShoppingCart,
    meta: { title: 'Carrito de Compras' }
  },
  {
    path: "/iniciar-sesion",
    name: "LoginForm",
    component: LoginForm,
    meta: { redirectIfAuth: true, title: 'Iniciar Sesión' }
  },
  {
    path: "/crear-cuenta",
    name: "RegisterForm",
    component: RegisterForm,
    meta: { redirectIfAuth: true, title: 'Crear Cuenta' }
  },
  {
    path: "/verificar-email",
    name: "EmailVerification",
    component: EmailVerificationPage,
    meta: { title: 'Verificar Email' }
  },
  {
    path: "/recuperar-contrasena",
    name: "PasswordReset",
    component: PasswordResetPage,
    meta: { redirectIfAuth: true, title: 'Recuperar Contraseña' }
  },
  {
    path: '/payment/result',
    name: 'PaymentResult',
    component: () => import('@/pages/PaymentResult.vue'),
    meta: { requiresAuth: true, title: 'Resultado del Pago' }
  },
  {
    path: '/configuracion/ventas/:id/imprimir',
    name: 'PrintOrder',
    component: () => import('@/pages/admin/PrintOrderPage.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Imprimir Pedido' }
  },
  {
    path: "/sucursales",
    name: "BranchLocator",
    component: () => import('@/pages/BranchLocator.vue'),
    meta: { title: 'Sucursales Correo Argentino' }
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/"
  }
];

const scrollPageToTop = () => {
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
};

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }

    if (to.hash) {
      return {
        el: to.hash,
        top: 80,
        behavior: 'auto',
      };
    }

    return { left: 0, top: 0 };
  },
});

  // SOLUCIÓN CORREGIDA PARA ROUTER GUARD - PRIORIDAD ABSOLUTA PARA ADMINS
router.beforeEach(async (to, from, next) => {
  // PASO 1: RUTAS DE ACCESO LIBRE (LOGIN, REGISTER, MANTENIMIENTO)
  // Si estamos yendo a la página de mantenimiento, siempre permitir
  if (to.name === 'Maintenance') {
    return next();
  }

  // Si estamos yendo a la página de login o register, siempre permitir
  if (to.name === 'LoginForm' || to.name === 'RegisterForm' || to.name === 'EmailVerification') {
    return next();
  }
  
  // PASO 2: VERIFICAR ESTADO DE AUTENTICACIÓN 
  // Verificar autenticación directamente desde localStorage
  const accessToken = localStorage.getItem('accessToken');
  const isAuthenticated = !!accessToken;
  const isAdmin = localStorage.getItem('isAdmin') === 'true';
  
  console.log('[Router] Estado de autenticación:', { isAuthenticated, isAdmin });
  
  // PASO 3: VERIFICAR EXPLÍCITAMENTE EL ESTADO DE ADMIN
  if (isAuthenticated) {
    try {
      // Forzar verificación explícita del estado de admin desde el servidor
      const adminService = (await import('@/services/auth')).default;
      const adminStatus = await adminService.isAdmin(true); // Forzar verificación
      
      console.log('[Router] Verificación explícita de admin:', adminStatus);
      
      // ACTUALIZAR estado local basado en la verificación del servidor
      localStorage.setItem('isAdmin', adminStatus ? 'true' : 'false');
      
      // Si es admin, SIEMPRE permitir acceso sin verificar mantenimiento
      if (adminStatus) {
        console.log('[Router] ADMIN VERIFICADO, permitiendo acceso sin restricciones');
        // FORZAR desactivación del modo mantenimiento para administradores
        localStorage.setItem('maintenance_mode', 'false');
        return next(); // Acceso directo sin más verificaciones
      }
    } catch (error) {
      console.error('[Router] Error verificando admin:', error);
    }
  }
  
  // PASO 4: VERIFICACIÓN DE MANTENIMIENTO PARA NO-ADMINS
  try {
    // Siempre permitir el acceso a la página de mantenimiento
    if (to.name === 'Maintenance') {
      return next();
    }
    
    // Importar servicio de mantenimiento
    const maintenanceService = (await import('@/services/maintenanceService')).default;
    
    // Verificar si el sitio está en modo mantenimiento
    const inMaintenance = await maintenanceService.isInMaintenance();
    console.log('[Router] Estado de mantenimiento:', inMaintenance);
    
    if (inMaintenance) {
      // Verificar si es admin o tiene bypass válido
      // IMPORTANTE: Todos los usuarios (autenticados o no) deben redirigirse a mantenimiento
      // a menos que sean admin o tengan bypass validado
      const hasValidBypass = maintenanceService.hasValidatedBypass();
      
      // Si no es bypass permitido, redirigir a página de mantenimiento
      if (!hasValidBypass) {
        console.log('[Router] Modo mantenimiento activo, redirigiendo a /mantenimiento');
        return next({ name: 'Maintenance' });
      } else {
        console.log('[Router] Usuario tiene bypass válido, permitiendo acceso');
      }
    }
  } catch (error) {
    console.error('[Router] Error verificando mantenimiento:', error);
    // En caso de error, continuamos con la navegación normal
  }
  
  // VERIFICACIÓN DE AUTENTICACIÓN Y PERMISOS
  // Si la ruta requiere autenticación y el usuario no está autenticado
  if (to.meta.requiresAuth && !isAuthenticated) {
    console.log('[Router] Redirigiendo a login (requiere autenticación)');
    localStorage.setItem('intendedRoute', to.fullPath);
    return next({
      name: 'LoginForm',
      query: { redirect: to.fullPath }
    });
  }

  // Si la ruta requiere admin y el usuario no es admin
  if (to.meta.requiresAdmin && !isAdmin) {
    console.log('[Router] Redirigiendo a pedidos (requiere admin)');
    return next('/configuracion/my-orders');
  }

  // Si todo está bien, permitir navegación
  next();
});

// Actualizar document.title y forzar scroll al inicio en cada navegación
router.afterEach((to) => {
  const title = to.meta.title;
  document.title = title ? `${title} | ${SITE_NAME}` : SITE_NAME;

  if (to.hash) return;

  // Refuerzo tras redirects, rutas anidadas y componentes async
  requestAnimationFrame(() => {
    scrollPageToTop();
  });
});

export default router;
